import * as React from "react";
import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Callout,
  FlexRow,
  H1,
  H2,
  H3,
  IconDiamond,
  IconPencil,
  Link,
  List,
  Stack,
  Text,
  WalletList,
} from "atoms";
import { Thumbnail } from "features/images";
import iconCheckRounded from "atoms/icons/check_rounded.svg";
import { constants, featureFlags } from "config";
import { formatDate } from "utils/date";
import { User, getPlatformRoles, useAuth } from "features/auth";
import { ArtistInvitesSidebar, OwnedReviewable } from "features/reviewable";
import { UserFollows } from "features/follows";
import { useAdminUserRefresh, useAdminUserUpdate } from "features/admin";
import { HUGS } from "../data/hugpasses";
import { DiscordConnect } from "./DiscordConnect";
import { LinkedTokenIds } from "./LinkedTokenIds";
import { ProfileDiscord } from "./ProfileDiscord";
import { ProfileReviews } from "../../reviews/components/ProfileReviews";
import { useProfilePoints } from "../api/useProfile";
import { ProfileProgress } from "./ProfileProgress";
import { ProfileRewards } from "./ProfileRewards";
import { useProfileApplications } from "../api/useProfileApplications";

type ProfileParams = {
  user: User;
};

const safeStringArray = (s: string[] | undefined) =>
  (s && s.length > 0 ? s : ["None"]).join(", ");

function Profile({ user }: ProfileParams) {
  const { pathname } = useLocation();
  const isAdminDetailsPage = pathname.startsWith("/admin/users/");

  const preferredHug = HUGS[user.hugImageIds?.[0] ?? 0];
  let avatarSrc: string;
  if (user.avatar) {
    avatarSrc = user.avatar.url;
  } else {
    avatarSrc = `https://thehug.xyz/assets/tokens/${preferredHug.image}`;
  }

  const { data: profilePoints } = useProfilePoints(user.id);
  const { data: applications } = useProfileApplications(user.id);

  const { adminUserAddPlatformRole, adminUserRemovePlatformRole } =
    useAdminUserUpdate();

  const adminRefreshUser = useAdminUserRefresh();
  const { refreshAuth } = useAuth();

  const refreshRoles = () => {
    if (isAdminDetailsPage) {
      adminRefreshUser(user.id);
    } else {
      refreshAuth();
    }
  };

  const { isCurator, isSrCurator, isHugger, isPoster } = React.useMemo(
    () => getPlatformRoles(user),
    [user]
  );

  let featuredRole = "Collector";
  if (isSrCurator) {
    featuredRole = "Senior Curator";
  } else if (isCurator) {
    featuredRole = "Curator";
  }

  const userIsArtist = !!user.claimedArtistPageIds?.length || user.creatorOptIn;

  const pointsLifetime = profilePoints?.lifetimeEarnings?.toLocaleString();
  const pointsBalance = profilePoints?.pointsBalance?.toLocaleString();

  const reviewsToSenior =
    constants.reviewsForSeniorCurator - (user.reviewCount ?? 0);

  const showAddPoster =
    isAdminDetailsPage && !!user.claimedArtistPageIds?.length && !isPoster;

  const showRemovePoster =
    isAdminDetailsPage && !!user.claimedArtistPageIds?.length && isPoster;

  const showApplyNowCopy =
    !user.claimedArtistPageIds?.length &&
    user.creatorOptIn &&
    !applications?.data?.length;

  return (
    <main>
      <ProfileColumns>
        <ProfileSide>
          <ProfileHeader>
            <AvatarWrapper>
              <Avatar
                alt={preferredHug.description}
                src={avatarSrc}
                size={100}
              />
            </AvatarWrapper>

            <Stack gap="xs">
              <Name size="xl">
                {user.name ? user.name : "Anonymous Hugger"}
              </Name>

              <Text size="xs" style={{ lineHeight: 1.3 }}>
                {userIsArtist && "Artist | "}
                {featuredRole}
                {(isCurator || isSrCurator) && `, Level ${user.curatorLevel}`}
              </Text>

              {isCurator && reviewsToSenior > 0 && (
                <Alert>
                  <Text>
                    Write {reviewsToSenior} more review
                    {reviewsToSenior !== 1 ? "s" : ""} to get promoted to Senior
                    Curator, and unlock the ability to moderate reviews.
                  </Text>
                </Alert>
              )}

              {/*
              TODO:

              if Curator:
                You only need {#} more HUG Points to get to level {#+1). Continue earning HUG Points by leaving reviews and voting for artists!

              if Senior Curator:
                "You only need {#} more HUG Points to get to level {#+1). Continue earning HUG Points by leaving and moderating reviews, and voting for artists!"

              */}

              {/* TODO: make box around this block */}
              {(!user.name || !user.email) && (
                <FinishProfile>
                  <Text size="xxs" bold>
                    Finish setting up your account
                  </Text>

                  <FinishProfileList>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked /> Connect wallet
                    </Text>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked={!!user.name} />
                      Create display name
                    </Text>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked={!!user.email} />
                      Add email address
                    </Text>
                  </FinishProfileList>
                </FinishProfile>
              )}

              {!isAdminDetailsPage && (
                <>
                  <EditProfile
                    to="edit"
                    title="Account settings"
                    variant="blank"
                  >
                    <IconPencil aria-hidden="true" /> Account settings
                  </EditProfile>
                  <Text>
                    <Link size="xxs" to={`/users/${user?.id}`}>
                      View Public Profile
                    </Link>
                  </Text>
                </>
              )}
            </Stack>
          </ProfileHeader>
        </ProfileSide>

        <ProfileMain>
          <ProfileProgress
            currentLevel={user.curatorLevel}
            isHugger={isHugger}
            isSrCurator={isSrCurator}
            lifetimePoints={profilePoints?.lifetimeEarnings ?? 0}
          />
        </ProfileMain>
      </ProfileColumns>

      <ProfileColumnsReverseSmall>
        <ProfileSide gap="lg">
          {isAdminDetailsPage && (
            <SideSection>
              <SectionHeading>Admin</SectionHeading>

              <Text size="xxs">
                <strong>Email</strong>
                <br />
                {user.email ?? "No Email"}
              </Text>

              <Text size="xxs">
                <strong>Hug Token Ids</strong>
                <br />
                <LinkedTokenIds tokenIds={user.hugTokenIds} />
              </Text>

              <Text size="xxs">
                <strong>Platform Roles</strong>
                <br />
                {safeStringArray(user.roles)}
              </Text>

              <Text size="xxs">
                <strong>Platform Id</strong>
                <br />
                {user.id}
              </Text>

              {showAddPoster && (
                <>
                  <Text bold>Artist Posts</Text>
                  <Button
                    variant="secondary"
                    onClick={() => adminUserAddPlatformRole(user, "poster")}
                  >
                    Allow Artist Posts
                  </Button>
                </>
              )}

              {isPoster && <Text bold>Artist Posts - Allowed</Text>}

              {showRemovePoster && (
                <Button
                  variant="secondary"
                  onClick={() => adminUserRemovePlatformRole(user, "poster")}
                >
                  Remove Posting
                </Button>
              )}

              <Text size="xxs">
                <Link to="#rewards-ledger">View Rewards Ledger</Link>
              </Text>
            </SideSection>
          )}

          <SideSection>
            <SectionHeading>Artist Invites</SectionHeading>
            <ArtistInvitesSidebar id={user.id} />
          </SideSection>

          {profilePoints && (
            <PointsSideSection>
              <SectionHeading>HUG Points</SectionHeading>
              {isHugger ? (
                <PointsSidebar>
                  <PointValue>
                    <IconDiamond accent="accent1" size="16px" />{" "}
                    {pointsLifetime}
                  </PointValue>
                  <PointLabel>Lifetime Earnings</PointLabel>

                  <PointValue>
                    <IconDiamond accent="accent1" size="16px" /> {pointsBalance}
                  </PointValue>
                  <PointLabel>Current Balance</PointLabel>
                </PointsSidebar>
              ) : (
                <>
                  <PointValue>
                    <IconDiamond accent="accent1" size="16px" />{" "}
                    {pointsLifetime} Unclaimed HUG Points
                  </PointValue>
                  <Text size="xxs">
                    {/* TODO: Mention that you can still spend HUG Points for reviews */}
                    You need a HUG Pass to claim and spend your HUG&nbsp;Points
                  </Text>

                  <Link
                    href={constants.openseaURL}
                    target="_blank"
                    variant="primary"
                    size="xxs"
                  >
                    Buy a HUG Pass
                  </Link>
                </>
              )}
            </PointsSideSection>
          )}

          <SideSection>
            <SectionHeading>Wallets</SectionHeading>
            <WalletList wallets={user.wallets} />
            <Link to="/profile/edit#wallet" size="xs">
              Add a wallet
            </Link>
          </SideSection>

          <SideSection>
            <SectionHeading>Discord</SectionHeading>
            {/* TODO: Discord connect infinite loading issue with location hash. */}
            {isAdminDetailsPage ? (
              <ProfileDiscord user={user} viewerIsAdmin={isAdminDetailsPage} />
            ) : (
              <DiscordConnect />
            )}
            <Button onClick={refreshRoles} size="xxxs">
              Refresh Roles
            </Button>
          </SideSection>

          {!!user?.guestPasses?.length && (
            <SideSection>
              <SectionHeading>Guest Passes</SectionHeading>
              {user.guestPasses.map((p) => p.toLowerCase()).join(",")}
            </SideSection>
          )}
        </ProfileSide>

        <ProfileMain>
          <Stack gap="lg">
            <Stack gap="sm">
              <SectionHeading>Artists I Follow</SectionHeading>
              <UserFollows
                userId={user.id}
                allowUpdates={!isAdminDetailsPage}
              />
            </Stack>

            {userIsArtist && (
              <Stack gap="sm">
                <SectionHeading id="artist-profiles">
                  My Artist Profile
                  {user?.claimedArtistPageIds?.length !== 1 ? "s" : ""}
                </SectionHeading>

                {(!!user.claimedArtistPageIds?.length || !!applications) && (
                  <List type="blank">
                    <Stack gap="sm">
                      {applications?.data?.map((application) => (
                        <li key={application.artistName}>
                          <ApplicationBox>
                            <FlexRow>
                              <ThumbnailWrapper>
                                <Thumbnail
                                  src={application.profileImage}
                                  entityType="ARTIST"
                                  width="60"
                                  height="60"
                                />
                              </ThumbnailWrapper>
                              <Stack gap="xxs">
                                <H3 size="sm" style={{ margin: 0 }}>
                                  <FlexRow flexWrap="wrap" gap="2px 8px">
                                    {application.artistName}
                                    <ApplicationPending>
                                      Application Pending
                                    </ApplicationPending>
                                  </FlexRow>
                                </H3>
                                <Text size="xxxs">
                                  Applied on{" "}
                                  {formatDate(application.createdAt, {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </Text>
                              </Stack>
                            </FlexRow>
                          </ApplicationBox>
                        </li>
                      ))}

                      {user?.claimedArtistPageIds?.map((id) => (
                        <li key={id}>
                          <OwnedReviewable id={id} />
                        </li>
                      ))}
                    </Stack>
                  </List>
                )}

                {showApplyNowCopy && (
                  <Callout center>
                    <H3 size="md">
                      Oh no!
                      <br />
                      You don&apos;t have a HUG Artist Profile yet.
                    </H3>

                    <Text>
                      Being an Artist on HUG makes it easier for you to get
                      seen, shared, and celebrated.
                    </Text>
                    <Text>
                      <Link
                        variant="primary"
                        to="/artist-profile-benefits"
                        size="sm"
                      >
                        Learn more and apply
                      </Link>
                    </Text>
                    <br />
                    <Text size="xxs">
                      If you have already applied, your application is currently
                      undergoing community voting, and you&apos;ll be notified
                      of the decision via email within 3-4 weeks of application.
                      If you have questions about your application status,{" "}
                      <Link to="/support">contact us</Link>.
                    </Text>
                  </Callout>
                )}
              </Stack>
            )}

            <Stack gap="sm">
              <SectionHeading id="reviews">My Reviews</SectionHeading>
              <ProfileReviews userId={user.id} />
            </Stack>

            {featureFlags.enableSBTRewards && <ProfileRewards user={user} />}
          </Stack>
        </ProfileMain>
      </ProfileColumnsReverseSmall>
    </main>
  );
}

const ProfileColumns = styled.div(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    justifyContent: "space-between",
    display: "grid",
    gap: theme.spacing.xl,
    gridTemplateColumns: "clamp(200px, 22vw, 350px) 1fr",
    gridTemplateAreas: "'side main'",
  },
  [theme.breakpoints.desktopMedium]: {
    gap: theme.spacing.xxl,
  },
  "&+*": {
    paddingTop: 0,
  },
}));

const ProfileSide = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing.lg,
  [theme.breakpoints.desktop]: {
    gridArea: "side",
    marginBottom: theme.spacing.xl,
  },
}));

const ProfileMain = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing.lg,
  [theme.breakpoints.desktop]: {
    gridArea: "main",
    marginBottom: theme.spacing.xl,
  },
}));
ProfileMain.defaultProps = {
  gap: "lg",
};

const ProfileColumnsReverseSmall = styled(ProfileColumns)(({ theme }) => ({
  paddingTop: 0,
  [theme.breakpoints.maxDesktop]: {
    display: "flex",
    flexDirection: "column-reverse",
  },
}));

const SideSection = styled(Stack)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  lineHeight: "1.2",
}));
SideSection.defaultProps = {
  gap: "xs",
};

const PointsSideSection = styled(SideSection)(({ theme }) => ({
  [theme.breakpoints.maxDesktop]: {
    display: "none",
  },
}));

const SectionHeading = styled(H2)(({ theme }) => ({
  fontSize: theme.fontSizes.md,
  fontWeight: "bold",
  borderBottom: `1px solid ${theme.colors.fg20}`,
  marginBottom: theme.spacing.sm,
  marginTop: "0 !important",
  paddingBottom: theme.spacing.xs,
}));

const ProfileHeader = styled.div(({ theme }) => ({
  "&>div:last-of-type": {
    flex: "1 0 0%",
  },
  p: {
    marginBottom: 0,
  },
  [theme.breakpoints.xs]: {
    alignItems: "center",
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-start",
  },
  [theme.breakpoints.desktop]: {
    display: "block",
  },
}));

const AvatarWrapper = styled.div(({ theme }) => ({
  alignSelf: "flex-start",
  position: "relative",
  marginBottom: "0.5rem",
  img: {
    display: "block",
  },
  [theme.breakpoints.desktop]: {
    marginBottom: theme.spacing.md,
  },
}));

const Name = styled(H1)({
  margin: "0 0 5px 0",
  lineHeight: "1.1",
});

const Alert = styled.div(({ theme }) => ({
  padding: "1rem",
  fontSize: theme.fontSizes.xxs,
  fontStyle: "italic",
  lineHeight: 1.25,
  borderRadius: theme.borderRadius.sm,
  backgroundColor: theme.colors.fg10,
}));

const EditProfile = styled(Link)(({ theme }) => ({
  alignItems: "center",
  color: theme.colors.fg,
  display: "inline-flex",
  fontSize: theme.fontSizes.xxs,
  fontWeight: "bold",
  gap: 5,
  lineHeight: 1.3,
  marginTop: "1em",
  textDecoration: "none",
  borderBottom: "1px solid currentColor",
  svg: {
    width: 11,
    height: "auto",
  },
}));

const Points = styled.dl({
  columns: 3,
  display: "block",
  maxWidth: 440,
});

const PointsSidebar = styled(Points)(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    alignItems: "center",
    columns: "none",
    display: "grid",
    gap: `10px ${theme.spacing.sm}`,
    gridTemplateColumns: "auto 1fr",
    paddingTop: 4,
  },
}));

const PointLabel = styled.dd(({ theme }) => ({
  lineHeight: "1.2",
  fontWeight: "normal",
  fontSize: theme.fontSizes.xxxs,
  marginBottom: 0,
  width: "min-content",
  flex: "1",
  "@media (min-width: 440px)": {
    width: "auto",
  },
}));

const PointValue = styled.dt(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  [theme.breakpoints.desktop]: {
    flex: "0 0 4ch",
    marginBottom: 0,
  },
}));

const FinishProfile = styled.div({
  padding: ".5em 0 .5em 0",
});

const FinishProfileList = styled.ul({
  listStyleType: "none",
  margin: ".5em 0 0",
  padding: 0,
  li: {
    display: "flex",
    alignItems: "center",
    margin: ".3em 0",
  },
});

const DisplayCheckbox = styled.span<{ checked: boolean }>(
  ({ checked, theme }) => ({
    display: "inline-flex",
    borderRadius: theme.borderRadius.xs,
    backgroundColor: theme.colors.fg10,
    width: "1em",
    height: "1em",
    marginRight: 10,
    ...(!checked && {
      backgroundColor: theme.colors.fg10,
      opacity: 0.5,
      mixBlendMode: "multiply",
    }),
    "&::after": {
      content: "''",
      backgroundImage: `url('${iconCheckRounded}')`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      display: "inline-block",
      height: "100%",
      width: "100%",
      transform: "translateX(.2em) scale(1.15)",
      opacity: 0.9,
      ...(!checked && {
        filter: "none",
        opacity: 0.5,
      }),
    },
  })
);

const ProfileStack = Stack;
ProfileStack.defaultProps = {
  gap: "xs",
};

const ThumbnailWrapper = styled.div({
  width: 60,
  height: 60,
  flex: "0 0 60px",
  alignSelf: "flex-start",
  "&+*": {
    flex: 1,
  },
});

const ApplicationBox = styled(Box)(({ theme }) => ({
  padding: "1rem",
  border: "1px solid",
  borderColor: theme.colors.accent3L,
}));

const ApplicationPending = styled.small(({ theme }) => ({
  display: "inline-block",
  padding: ".2em .5em",
  backgroundColor: theme.colors.accent3LL,
  borderRadius: theme.borderRadius.sm,
}));

export { Profile };
